import { Controller, Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import React from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import AddressAutocomplete from '@components/common/AddressAutocomplete';
import type { ServiceType } from '@app-types/models/plan';
import type { AreaRegion, AreaLocation } from '@app-types/models/area';
import type { AreaSuggestionProps } from '@components/common/AreaSuggestion';

export interface PlaceSelectedResult {
  address: string;
  coordinates: number[] | null;
  place_id?: string;
}

export interface Step1FormTranslations {
  companyName: string;
  businessName: string;
  address: string;
  region: string;
  businessArea: string;
  serviceTypes: string;
  socialMediaLink: string;
  additional: string;
  referredByCode: string;
  br: string;
  storePhoto: string;
  proof: string;
  uploadImages: string;
  iAccept: string;
  termsAndConditions: string;
  privacyPolicy: string;
  error: string;
  [key: string]: string;
}

interface SelectedFiles {
  br: File[];
  storePhoto: File[];
  proof: File[];
  [key: string]: File[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFormControl = Control<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFieldErrors = FieldErrors<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySetValue = UseFormSetValue<any>;

export interface Step1FormProps {
  control: AnyFormControl;
  errors: AnyFieldErrors;
  serviceTypes: ServiceType[];
  selectedFiles: SelectedFiles;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  renderSelectedFiles: (files: File[], fieldName: string) => React.ReactNode;
  setValue: AnySetValue;
  isTncAccepted: boolean;
  setIsTncAccepted: (accepted: boolean) => void;
  isPrivacyPolicyAccepted: boolean;
  setIsPrivacyPolicyAccepted: (accepted: boolean) => void;
  handleTncDialogOpen: () => void;
  handlePrivacyPolicyDialogOpen: () => void;
  handleNextStep: () => void;
  error?: string;
  t: Step1FormTranslations;
  onPlaceSelected?: (placeData: PlaceSelectedResult) => void;
  currentPlaceId?: string;
  currentAddress?: string;
  GoogleMapsLinks?: React.ComponentType<{ placeId: string; address: string }>;
  areaSuggestion?: AreaSuggestionProps['matchResult'];
  onAreaSuggestionAccept?: AreaSuggestionProps['onAcceptSuggestion'];
  onAreaSuggestionDismiss?: () => void;
  AreaSuggestion?: React.ComponentType<AreaSuggestionProps>;
  areaData?: AreaRegion[];
  availableLocations?: AreaLocation[];
  selectedRegion?: string;
  handleRegionChange?: (e: SelectChangeEvent<string>) => void;
  currentLanguage?: string;
}

const Step1Form: React.FC<Step1FormProps> = ({
  control,
  errors,
  serviceTypes,
  selectedFiles,
  handleFileChange,
  renderSelectedFiles,
  setValue,
  isTncAccepted,
  setIsTncAccepted,
  isPrivacyPolicyAccepted,
  setIsPrivacyPolicyAccepted,
  handleTncDialogOpen,
  handlePrivacyPolicyDialogOpen,
  handleNextStep,
  error,
  t,
  onPlaceSelected,
  currentPlaceId,
  currentAddress,
  GoogleMapsLinks,
  areaSuggestion,
  onAreaSuggestionAccept,
  onAreaSuggestionDismiss,
  AreaSuggestion,
  areaData = [],
  availableLocations = [],
  selectedRegion,
  handleRegionChange,
  currentLanguage
}) => {
  return (
    <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Controller
              name="companyName"
              control={control}
              rules={{ required: 'Company name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t.companyName}
                  type="text"
                  required
                  error={!!errors.companyName}
                  helperText={errors.companyName ? t.error + (errors.companyName.message || 'Company name is required') : ''}
                  autoComplete="organization"
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Controller
              name="businessName"
              control={control}
              rules={{ required: 'Business name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t.businessName}
                  type="text"
                  required
                  error={!!errors.businessName}
                  helperText={errors.businessName ? t.error + (errors.businessName.message || 'Business name is required') : ''}
                  autoComplete="organization"
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Controller
              name="address"
              control={control}
              rules={{ required: 'Address is required' }}
              render={({ field }) => (
                <>
                  <AddressAutocomplete
                    label={`${t.address} *`}
                    value={field.value}
                    onInputChange={(value: string) => {
                      field.onChange(value);
                    }}
                    onPlaceSelected={(placeData: PlaceSelectedResult) => {
                      console.log('[Step1Form] onPlaceSelected called with:', placeData);
                      if (placeData.coordinates && Array.isArray(placeData.coordinates)) {
                        setValue('coordinates', placeData.coordinates);
                      }
                      field.onChange(placeData.address);
                      if (onPlaceSelected) {
                        onPlaceSelected(placeData);
                      }
                    }}
                    error={!!errors.address}
                  />
                  {currentPlaceId && currentAddress && GoogleMapsLinks && (
                    <GoogleMapsLinks placeId={currentPlaceId} address={currentAddress} />
                  )}
                  {areaSuggestion && AreaSuggestion && onAreaSuggestionAccept && onAreaSuggestionDismiss && (
                    <AreaSuggestion
                      matchResult={areaSuggestion}
                      onAcceptSuggestion={onAreaSuggestionAccept}
                      onDismiss={onAreaSuggestionDismiss}
                      currentSelection=""
                      showDebugInfo={false}
                    />
                  )}
                </>
              )}
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth margin="normal">
                <InputLabel>{t.region || 'Region'}</InputLabel>
                <Controller
                    name="region"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            label={t.region || 'Region'}
                            value={selectedRegion}
                            onChange={(e) => {
                                field.onChange(e);
                                if (handleRegionChange) handleRegionChange(e);
                            }}
                        >
                            {(areaData || []).map((region) => (
                                <MenuItem key={region._id} value={region._id}>
                                    {currentLanguage === 'zh' ? region.region_chinese : region.region_english}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth margin="normal">
                <InputLabel>{t.businessArea || 'Business Area'}</InputLabel>
                <Controller
                    name="businessArea"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            label={t.businessArea || 'Business Area'}
                            disabled={!selectedRegion}
                        >
                            {(availableLocations || []).map((location) => (
                                <MenuItem key={location._id} value={location._id}>
                                    {currentLanguage === 'zh' ? location.chinese : location.english}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal" variant="outlined" error={!!errors.selectedServiceTypes}>
            <InputLabel id="service-types-label">{t.serviceTypes} *</InputLabel>
            <Controller
              name="selectedServiceTypes"
              control={control}
              rules={{
                required: 'Service types are required',
                validate: (value) => {
                  if (!value || !Array.isArray(value) || value.length === 0) {
                    return 'Please select at least one service type';
                  }
                  return true;
                }
              }}
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="service-types-label"
                  label={`${t.serviceTypes} *`}
                  multiple
                  required
                  value={field.value || []}
                  error={!!errors.selectedServiceTypes}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    setValue('selectedServiceTypes', value as string[]);
                    setValue(
                      'addedServiceTypes',
                      (serviceTypes || []).filter((type) => ((value as string[]) || []).includes(type._id))
                    );
                  }}
                  renderValue={(selected) =>
                    ((selected as string[]) || [])
                      .map((id) => {
                        const type = serviceTypes.find((type) => type._id === id);
                        return type ? `${type.zh_name || type.name} (${type.name})` : '';
                      })
                      .join(', ')
                  }
                >
                  {(serviceTypes || []).map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                      {type.zh_name || type.name} ({type.name})
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.selectedServiceTypes && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {String(errors.selectedServiceTypes.message || '')}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Controller
              name="socialMediaLink"
              control={control}
              rules={{
                required: 'Social media link is required',
                pattern: {
                  value: /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
                  message: 'Please enter a valid URL'
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t.socialMediaLink}
                  type="url"
                  required
                  error={!!errors.socialMediaLink}
                  helperText={errors.socialMediaLink ? String(errors.socialMediaLink.message || '') : ''}
                  autoComplete="url"
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Controller
              name="additional"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t.additional}
                  multiline
                  rows={4}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Controller
              name="referredByCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t.referredByCode}
                  type="text"
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Typography variant="body1">{t.br}</Typography>
            <Button
              variant="contained"
              component="label"
            >
              {t.uploadImages}
              <input
                type="file"
                id="br"
                name="br"
                onChange={handleFileChange}
                multiple
                hidden
              />
            </Button>
            {renderSelectedFiles(selectedFiles.br, 'br')}
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Typography variant="body1">{t.storePhoto}</Typography>
            <Button
              variant="contained"
              component="label"
            >
              {t.uploadImages}
              <input
                type="file"
                id="storePhoto"
                name="storePhoto"
                onChange={handleFileChange}
                multiple
                hidden
              />
            </Button>
            {renderSelectedFiles(selectedFiles.storePhoto, 'storePhoto')}
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <Typography variant="body1">{t.proof}</Typography>
            <Button
              variant="contained"
              component="label"
            >
              {t.uploadImages}
              <input
                type="file"
                id="proof"
                name="proof"
                onChange={handleFileChange}
                multiple
                hidden
              />
            </Button>
            {renderSelectedFiles(selectedFiles.proof, 'proof')}
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isTncAccepted}
                  onChange={(e) => setIsTncAccepted(e.target.checked)}
                  name="tnc"
                  color="primary"
                />
              }
              label={
                <span>
                  {t.iAccept} <Button onClick={handleTncDialogOpen} color="primary">{t.termsAndConditions}</Button>
                </span>
              }
            />
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth margin="normal">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPrivacyPolicyAccepted}
                  onChange={(e) => setIsPrivacyPolicyAccepted(e.target.checked)}
                  name="privacyPolicy"
                  color="primary"
                />
              }
              label={
                <span>
                  {t.iAccept} <Button onClick={handlePrivacyPolicyDialogOpen} color="primary">{t.privacyPolicy}</Button>
                </span>
              }
            />
          </FormControl>
        </Grid>

        <Grid size={12}>
          {error && <Typography color="error">{error}</Typography>}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
          >
            Next: Step 2
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default Step1Form;
